import React, { ReactElement } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";

interface IMultiSelectDropdown {
    options: Array<{ key: string; text: string }>;
    placeholder?: string;
    className?: string;
    defaultSelectedOptions: string[];
    onOptionSelect: (selectedOptions: string[]) => void;
}

export function MultiSelectDropdown({
    options,
    className = "",
    placeholder = "Select...",
    defaultSelectedOptions = [],
    onOptionSelect
}: IMultiSelectDropdown): ReactElement {
    const [open, setOpen] = React.useState(false);
    const [selectedKeys, setSelectedKeys] = React.useState(defaultSelectedOptions);

    const toggleOption = (val) => {
        const next = selectedKeys.includes(val.key) ? selectedKeys.filter((v) => v !== val.key) : [...selectedKeys, val.key];
        setSelectedKeys(next);
        const selectedOptions: string[] = options.filter((opt) => next.includes(opt.key)).map((opt) => opt.key);
        onOptionSelect(selectedOptions);
    };

    // Always show placeholder text, optionally with selected count
    const label = selectedKeys.length > 0 ? `${placeholder} (${selectedKeys.length})` : placeholder;

    return (
        <DropdownMenu.Root open={open} onOpenChange={setOpen}>
            <DropdownMenu.Trigger asChild>
                <button
                    className={`px-3 py-2 text-left border border-gray-300 rounded bg-white hover:bg-gray-50 transition-colors ${className}`}
                    style={{
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: "120px"
                    }}
                >
                    {label}
                </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="min-w-[220px] bg-white rounded-md shadow-lg border border-gray-200 p-2 z-50"
                    sideOffset={4}
                    align="start"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="max-h-60 overflow-auto pr-1">
                        {options.map((opt, i) => {
                            const isChecked = selectedKeys.includes(opt.key);
                            return (
                                <DropdownMenu.CheckboxItem
                                    key={i}
                                    className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-gray-100 cursor-default"
                                    checked={isChecked}
                                    onCheckedChange={() => toggleOption(opt)}
                                    onSelect={(e) => e.preventDefault()}
                                >
                                    <div
                                        className="border border-gray-300 rounded bg-white"
                                        style={{ width: 16, height: 16, position: "relative", lineHeight: 0, flex: "0 0 auto" }}
                                    >
                                        {isChecked ? (
                                            <Check
                                                width={10}
                                                height={8}
                                                strokeWidth={2}
                                                style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    display: "block",
                                                    pointerEvents: "none"
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                    <span className="flex-1">{opt.text}</span>
                                </DropdownMenu.CheckboxItem>
                            );
                        })}
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
